/**
 * SearXNG Settings Component
 *
 * Global admin page for configuring SearXNG web search integration:
 * - SearXNG instance URL configuration
 * - Connection health check
 * - Default search settings
 * - Search engine configuration
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

interface SearXNGStatus {
  connected: boolean;
  jsonEnabled: boolean;
  url: string;
  error?: string;
  lastChecked: string;
}

interface SearXNGSettings {
  baseUrl: string;
  defaultMaxResults: number;
  defaultSafeSearch: number;
  defaultCategory: string;
  timeout: number;
  enabled: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const SAFE_SEARCH_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'Moderate' },
  { value: 2, label: 'Strict' },
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'news', label: 'News' },
  { value: 'images', label: 'Images' },
  { value: 'videos', label: 'Videos' },
  { value: 'science', label: 'Science' },
  { value: 'it', label: 'IT / Programming' },
];

// =============================================================================
// Sub-components
// =============================================================================

interface FormInputProps {
  label: string;
  id: string;
  type?: 'text' | 'number' | 'url';
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  min?: number;
  max?: number;
}

function FormInput({
  label,
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled,
  helpText,
  error,
  min,
  max,
}: FormInputProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        className={`w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      />
      {helpText && !error && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

interface FormSelectProps {
  label: string;
  id: string;
  value: string | number;
  onChange: (value: string) => void;
  options: Array<{ value: string | number; label: string }>;
  disabled?: boolean;
  helpText?: string;
}

function FormSelect({
  label,
  id,
  value,
  onChange,
  options,
  disabled,
  helpText,
}: FormSelectProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
    </div>
  );
}

interface FormToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function FormToggle({ label, description, checked, onChange, disabled }: FormToggleProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-gray-300">{label}</span>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-discord-blurple' : 'bg-discord-light'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

interface StatusBadgeProps {
  connected: boolean;
  jsonEnabled: boolean;
  checking: boolean;
}

function StatusBadge({ connected, jsonEnabled, checking }: StatusBadgeProps) {
  if (checking) {
    return (
      <div className="flex items-center gap-2 rounded-full bg-yellow-900/20 px-3 py-1.5 text-sm text-yellow-400">
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Checking...
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="flex items-center gap-2 rounded-full bg-red-900/20 px-3 py-1.5 text-sm text-red-400">
        <span className="h-2 w-2 rounded-full bg-red-400" />
        Disconnected
      </div>
    );
  }

  if (!jsonEnabled) {
    return (
      <div className="flex items-center gap-2 rounded-full bg-yellow-900/20 px-3 py-1.5 text-sm text-yellow-400">
        <span className="h-2 w-2 rounded-full bg-yellow-400" />
        JSON Not Enabled
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-full bg-green-900/20 px-3 py-1.5 text-sm text-green-400">
      <span className="h-2 w-2 rounded-full bg-green-400" />
      Connected
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function SearXNGSettings() {
  const { user } = useAuth();

  // State
  const [settings, setSettings] = useState<SearXNGSettings | null>(null);
  const [defaultUrl, setDefaultUrl] = useState<string>('');
  const [useCustomUrl, setUseCustomUrl] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const [status, setStatus] = useState<SearXNGStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user is global admin
  const isGlobalAdmin = user?.role === 'GLOBAL_ADMIN';

  /**
   * Fetch settings from API
   */
  const fetchSettings = useCallback(async () => {
    try {
      const apiClient = getApiClient();
      const result = await apiClient.get<{ settings: SearXNGSettings; defaultUrl: string }>('/searxng/settings');

      if (isSuccess(result)) {
        setSettings(result.data.settings);
        setDefaultUrl(result.data.defaultUrl);

        // Check if custom URL is being used
        if (result.data.settings.baseUrl !== result.data.defaultUrl) {
          setUseCustomUrl(true);
          setCustomUrl(result.data.settings.baseUrl);
        }
      } else {
        setError(result.error.message || 'Failed to load settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Check SearXNG connection status via API
   */
  const checkStatus = useCallback(async () => {
    setIsChecking(true);
    setError(null);

    try {
      const apiClient = getApiClient();
      const result = await apiClient.get<{ status: SearXNGStatus; defaultUrl: string }>('/searxng/status');

      if (isSuccess(result)) {
        setStatus(result.data.status);
      } else {
        setError(result.error.message || 'Failed to check status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection check failed');
    } finally {
      setIsChecking(false);
    }
  }, []);

  /**
   * Save settings to API
   */
  const handleSave = useCallback(async () => {
    if (!settings) return;

    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Determine which URL to use
      const urlToUse = useCustomUrl ? customUrl : defaultUrl;

      const apiClient = getApiClient();
      const result = await apiClient.put<{ settings: SearXNGSettings }>('/searxng/settings', {
        ...settings,
        baseUrl: urlToUse,
      });

      if (isSuccess(result)) {
        setSettings(result.data.settings);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);

        // Re-check status with new URL
        await checkStatus();
      } else {
        setError(result.error.message || 'Failed to save settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  }, [settings, useCustomUrl, customUrl, defaultUrl, checkStatus]);

  /**
   * Reset settings to defaults
   */
  const handleReset = useCallback(async () => {
    try {
      const apiClient = getApiClient();
      const result = await apiClient.post<{ settings: SearXNGSettings }>('/searxng/reset', {});

      if (isSuccess(result)) {
        setSettings(result.data.settings);
        setUseCustomUrl(false);
        setCustomUrl('');
        await checkStatus();
      } else {
        setError(result.error.message || 'Failed to reset settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset settings');
    }
  }, [checkStatus]);

  /**
   * Load settings on mount
   */
  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  /**
   * Check status after settings are loaded
   */
  useEffect(() => {
    if (settings) {
      void checkStatus();
    }
  }, [settings, checkStatus]);

  if (!isGlobalAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-12">
          <svg className="h-12 w-12 text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <h3 className="text-lg font-medium text-white">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-400">
            Only Global Administrators can access SearXNG settings.
          </p>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-discord-blurple" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">SearXNG Settings</h1>
            <p className="text-sm text-gray-400 mt-1">
              Configure the SearXNG web search integration
            </p>
          </div>
          <StatusBadge
            connected={status?.connected ?? false}
            jsonEnabled={status?.jsonEnabled ?? false}
            checking={isChecking}
          />
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg className="mr-3 h-5 w-5 flex-shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-200">{error}</p>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {saveSuccess && (
          <div className="rounded-md bg-green-900/50 border border-green-500 p-4">
            <div className="flex items-center">
              <svg className="mr-3 h-5 w-5 flex-shrink-0 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-200">Settings saved successfully!</p>
            </div>
          </div>
        )}

        {/* Connection Settings */}
        <div className="rounded-lg bg-discord-darker border border-discord-light p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">Connection Settings</h3>
            <p className="text-sm text-gray-400">Configure the SearXNG instance connection</p>
          </div>

          <FormToggle
            label="Enable SearXNG Integration"
            description="Enable or disable web search functionality globally"
            checked={settings?.enabled ?? true}
            onChange={(checked) => setSettings((s) => s ? { ...s, enabled: checked } : null)}
          />

          {/* Default URL Display */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Docker SearXNG URL (Default)
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1 rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-gray-400">
                {defaultUrl || 'Loading...'}
              </div>
              <span className="text-xs text-gray-500">Internal Docker network</span>
            </div>
          </div>

          {/* Override Checkbox */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="useCustomUrl"
              checked={useCustomUrl}
              onChange={(e) => {
                setUseCustomUrl(e.target.checked);
                if (!e.target.checked) {
                  setCustomUrl('');
                }
              }}
              disabled={!settings?.enabled}
              className="h-4 w-4 rounded border-discord-light bg-discord-darkest text-discord-blurple focus:ring-discord-blurple focus:ring-offset-discord-darker"
            />
            <label htmlFor="useCustomUrl" className="text-sm text-gray-300">
              Override with custom SearXNG URL
            </label>
          </div>

          {/* Custom URL Input (only shown when override is enabled) */}
          {useCustomUrl && (
            <FormInput
              label="Custom SearXNG URL"
              id="customUrl"
              type="url"
              value={customUrl}
              onChange={setCustomUrl}
              placeholder="http://your-searxng-instance:8080"
              helpText="Enter the URL of your external SearXNG instance"
              disabled={!settings?.enabled}
            />
          )}

          <FormInput
            label="Timeout (ms)"
            id="timeout"
            type="number"
            value={settings?.timeout ?? 10000}
            onChange={(value) => setSettings((s) => s ? { ...s, timeout: parseInt(value, 10) || 10000 } : null)}
            min={1000}
            max={60000}
            helpText="Request timeout in milliseconds (1000-60000)"
            disabled={!settings?.enabled}
          />

          <div className="flex gap-3">
            <button
              onClick={checkStatus}
              disabled={isChecking || !settings?.enabled}
              className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isChecking ? 'Checking...' : 'Test Connection'}
            </button>
          </div>

          {status && !status.connected && status.error && (
            <div className="rounded-md bg-red-900/30 p-3">
              <p className="text-sm text-red-300">{status.error}</p>
            </div>
          )}

          {status && status.connected && !status.jsonEnabled && (
            <div className="rounded-md bg-yellow-900/30 p-3">
              <p className="text-sm text-yellow-300">
                SearXNG is connected but JSON format is not enabled. Please enable JSON format in your SearXNG settings.yml:
              </p>
              <pre className="mt-2 text-xs text-yellow-200 bg-black/30 p-2 rounded overflow-x-auto">
{`search:
  formats:
    - html
    - json`}
              </pre>
            </div>
          )}
        </div>

        {/* Default Search Settings */}
        <div className="rounded-lg bg-discord-darker border border-discord-light p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">Default Search Settings</h3>
            <p className="text-sm text-gray-400">Configure default values for bot search operations</p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormInput
              label="Default Max Results"
              id="defaultMaxResults"
              type="number"
              value={settings?.defaultMaxResults ?? 5}
              onChange={(value) => setSettings((s) => s ? { ...s, defaultMaxResults: parseInt(value, 10) || 5 } : null)}
              min={1}
              max={20}
              helpText="Maximum search results per query (1-20)"
              disabled={!settings?.enabled}
            />

            <FormSelect
              label="Default Safe Search"
              id="defaultSafeSearch"
              value={settings?.defaultSafeSearch ?? 1}
              onChange={(value) => setSettings((s) => s ? { ...s, defaultSafeSearch: parseInt(value, 10) } : null)}
              options={SAFE_SEARCH_OPTIONS}
              helpText="Default content filtering level"
              disabled={!settings?.enabled}
            />

            <FormSelect
              label="Default Category"
              id="defaultCategory"
              value={settings?.defaultCategory ?? 'general'}
              onChange={(value) => setSettings((s) => s ? { ...s, defaultCategory: value } : null)}
              options={CATEGORY_OPTIONS}
              helpText="Default search category"
              disabled={!settings?.enabled}
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
          <h3 className="text-lg font-semibold text-white mb-3">About SearXNG Integration</h3>
          <div className="space-y-3 text-sm text-gray-400">
            <p>
              SearXNG is a free, privacy-respecting metasearch engine that aggregates results from multiple search engines without tracking.
            </p>
            <p>
              When enabled, bots can use the <code className="bg-discord-light px-1 rounded text-gray-300">/search</code> command to search the web.
              LLM endpoints with "Enable Web Search" can also autonomously trigger searches via tool calling.
            </p>
            <div className="mt-4">
              <h4 className="text-white font-medium mb-2">Configuration Files:</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>SearXNG settings: <code className="bg-discord-light px-1 rounded text-gray-300">config/searxng/settings.yml</code></li>
                <li>Docker Compose: <code className="bg-discord-light px-1 rounded text-gray-300">docker-compose.infra.yml</code></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <button
            onClick={handleReset}
            className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </Layout>
  );
}

export default SearXNGSettings;
